import React, { useState, useEffect } from 'react';
import './GrowthPartnerPage.css';
import Header from './Header';
import AnimatedBackground from './AnimatedBackground';

const GrowthPartnerPage = () => {
  const [growthFormSent, setGrowthFormSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGrowthFormSubmit = (e) => {
    e.preventDefault();
    setGrowthFormSent(true);
  };

  const phases = [
    {
      n: '01',
      title: 'Descubrir',
      desc: 'Diagnóstico del negocio, su mercado y su data actual. Definimos KPIs reales y un plan de ecosistema digital priorizado por impacto.',
      icon: 'search',
    },
    {
      n: '02',
      title: 'Construir',
      desc: 'Diseñamos y desarrollamos: web, tienda, formularios de captación, integraciones, branding y los flujos que mueven la aguja.',
      icon: 'cubes',
    },
    {
      n: '03',
      title: 'Escalar',
      desc: 'Operamos contigo. Medimos cada semana, iteramos con datos y multiplicamos lo que sí está funcionando. Acompañamiento mes a mes.',
      icon: 'rocket',
    },
  ];

  const cases = [
    {
      name: 'Strutech Solutions',
      sector: 'Diseño y construcción · Bogotá',
      url: 'https://strutechsolutions.com/',
      summary: 'Ecosistema digital completo para captación B2B y posicionamiento de marca en el sector de arquitectura e interiorismo.',
      tags: ['Diseño de marca', 'Web corporativa', 'Captación de leads', 'CRM y operación'],
      accent: 'gold',
    },
    {
      name: 'RGS Marketing Experience',
      sector: 'Marketing BTL y experiencias',
      url: 'https://rgsbtl.com/',
      summary: 'Rediseño completo del sitio web y creación de un formulario gancho para captar más leads. Renovamos su ecosistema digital de cero.',
      tags: ['Rediseño web 360°', 'Formulario gancho', 'Generación de leads'],
      accent: 'wine',
    },
    {
      name: 'Efecto Mariposa',
      sector: 'Educación y desarrollo personal',
      url: 'https://efectomariposa.life/',
      summary: 'Modificamos una de sus páginas existentes y creamos páginas nuevas para ofrecer 4 cursos adicionales dentro de su ecosistema actual.',
      tags: ['Páginas de cursos', 'Optimización web', '4 nuevos lanzamientos'],
      accent: 'forest',
    },
    {
      name: 'Nutrisetas',
      sector: 'E-commerce de bienestar · Shopify',
      url: 'https://www.nutrisetas.com/',
      summary: 'Tienda online de setas y hongos en Shopify. Trabajamos diseño de marca, seguridad de datos y posicionamiento SEO en Google para vender más.',
      tags: ['Diseño de marca', 'Seguridad de datos', 'SEO Google', 'Shopify'],
      accent: 'earth',
    },
  ];

  return (
    <div className="growth-partner-page">
      <Header />
      <AnimatedBackground particleCount={10} staticCount={3} />

      <main className="gp-main">
        {/* HERO */}
        <section className="gp-hero">
          <div className="gp-content-wrap">
            <div className="gp-shapes" aria-hidden="true">
              <div className="gp-shape gp-shape-1"></div>
              <div className="gp-shape gp-shape-2"></div>
              <div className="gp-shape gp-shape-3"></div>
              <div className="gp-shape gp-shape-4"></div>
            </div>
            <div className="gp-content">
              <span className="gp-tag">GROWTH PARTNER · JAVILAND</span>
              <h1 className="gp-headline">
                Ecosistemas digitales basados en datos para marcas que quieren crecer en serio.
              </h1>
              <p className="gp-narrative">
                Acompañamos a empresas que aún no tienen un ecosistema digital —o que tienen uno que no escala— y lo construimos contigo de principio a fin.
                Diseñamos, desarrollamos y operamos. Lo que medimos lo mejoramos.
              </p>
              <div className="gp-pillars">
                <span className="gp-pillar"><i className="fas fa-chart-line" /> Datos primero</span>
                <span className="gp-pillar"><i className="fas fa-cogs" /> Operación real</span>
                <span className="gp-pillar"><i className="fas fa-handshake" /> Largo plazo</span>
              </div>
              <div className="gp-hero-cta">
                <a href="#gp-form" className="gp-cta-primary">Cotizar mi proyecto</a>
                <a href="#gp-casos" className="gp-cta-ghost">Ver casos reales</a>
              </div>
            </div>
          </div>
        </section>

        {/* 3 FASES */}
        <section className="gp-phases-section">
          <div className="gp-section-wrap">
            <h2 className="gp-section-title">Cómo trabajamos: 3 fases claras</h2>
            <p className="gp-section-sub">
              Cada cliente entra en la fase que necesita. Lo ideal es empezar en Fase 1 y avanzar contigo, pero también recibimos proyectos en marcha.
            </p>
            <div className="gp-phases-grid">
              {phases.map((p) => (
                <article key={p.n} className="gp-phase-card">
                  <span className="gp-phase-num">{p.n}</span>
                  <div className="gp-phase-icon"><i className={`fas fa-${p.icon}`} /></div>
                  <h3 className="gp-phase-title">{p.title}</h3>
                  <p className="gp-phase-desc">{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CASOS */}
        <section id="gp-casos" className="gp-cases-section">
          <div className="gp-section-wrap">
            <h2 className="gp-section-title">Marcas que ya operan con nosotros</h2>
            <p className="gp-section-sub">
              Cada ecosistema es distinto. Estos son algunos clientes con los que estamos construyendo o ya entregamos resultados.
            </p>
            <div className="gp-cases-grid">
              {cases.map((c, i) => {
                const Tag = c.url ? 'a' : 'article';
                const props = c.url ? { href: c.url, target: '_blank', rel: 'noopener noreferrer' } : {};
                return (
                  <Tag key={i} {...props} className={`gp-case-card gp-case-card--${c.accent}`}>
                    <span className="gp-case-sector">{c.sector}</span>
                    <h3 className="gp-case-name">{c.name}</h3>
                    <p className="gp-case-summary">{c.summary}</p>
                    <div className="gp-case-tags">
                      {c.tags.map((tag, j) => (
                        <span key={j} className="gp-case-tag">{tag}</span>
                      ))}
                    </div>
                    {c.url ? (
                      <span className="gp-case-link">Visitar sitio <i className="fas fa-external-link-alt" /></span>
                    ) : (
                      <span className="gp-case-link gp-case-link--quiet">Caso operando · más datos a solicitud</span>
                    )}
                  </Tag>
                );
              })}
            </div>
            <p className="gp-cases-more">¿Quieres ver más casos? Tenemos referencias adicionales bajo NDA. Pregúntanos.</p>
          </div>
        </section>

        {/* FORM */}
        <section id="gp-form" className="gp-form-section-v2">
          <div className="gp-form-wrap">
            <div className="gp-form-copy">
              <h2 className="gp-form-title">¿Hablamos de tu ecosistema?</h2>
              <p className="gp-form-lead">
                Cuéntanos en una línea qué estás construyendo o qué te traba. Nosotros respondemos con un próximo paso concreto en menos de 48 horas hábiles.
              </p>
              <ul className="gp-form-points">
                <li><i className="fas fa-check" /> Diagnóstico inicial sin costo</li>
                <li><i className="fas fa-check" /> Cotización clara por fases</li>
                <li><i className="fas fa-check" /> Sin contratos atados de manos</li>
              </ul>
              <p className="gp-form-email">
                <i className="fas fa-envelope" /> O escríbenos directo a <a href="mailto:growthpartner@javiland.com">growthpartner@javiland.com</a>
              </p>
            </div>

            <div className="gp-form-card">
              {growthFormSent ? (
                <div className="gp-form-success">
                  <i className="fas fa-check-circle"></i>
                  <p>Gracias. Recibimos tu mensaje y te respondemos pronto desde <strong>growthpartner@javiland.com</strong>.</p>
                </div>
              ) : (
                <form className="gp-form" onSubmit={handleGrowthFormSubmit}>
                  <label className="gp-form-label">
                    <span>Nombre</span>
                    <input type="text" placeholder="Tu nombre" required />
                  </label>
                  <label className="gp-form-label">
                    <span>Email</span>
                    <input type="email" placeholder="correo@empresa.com" required />
                  </label>
                  <label className="gp-form-label">
                    <span>Empresa o marca</span>
                    <input type="text" placeholder="Nombre del negocio" />
                  </label>
                  <label className="gp-form-label">
                    <span>Cuéntanos tu proyecto</span>
                    <textarea placeholder="Qué tienes hoy, qué necesitas y dónde quieres llegar" rows="4" required></textarea>
                  </label>
                  <button type="submit" className="gp-form-btn">Quiero cotizar mi ecosistema</button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default GrowthPartnerPage;
